import { Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { FiSettings } from 'react-icons/fi';
import { SignalsHeaderProps } from './types';
import Select from 'components/Common/Select';
import DateRangePicker from 'components/Common/DateRangePicker';
import { IoListOutline } from 'react-icons/io5';
import { AiOutlineAppstore } from 'react-icons/ai';
import { SignalsHeaderMetricsSkeleton } from './SignalsSkeletons';

export default function SignalsHeader({
  control,
  viewMode,
  onViewModeChange,
  onOpenSettings,
  metricCards,
  isMetricsLoading,
  keywordOptions,
  createdAt
}: SignalsHeaderProps) {
  const theme = useTheme();

  const INSIGHTS_MIN_DATE = '2026-05-01';

  return (
    <Box mb={3}>
      {/* HEADER */}
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent='space-between' alignItems='center' gap={2} mb={3}>
        <Box>
          <Typography fontSize={20} fontWeight={500} lineHeight='28px'>
            Signals
          </Typography>
          <Typography color='text.secondary' fontSize={14}>
            Caller sentiment insights understand what people are saying about your business
          </Typography>
        </Box>

        <Stack direction='row' gap={1.5} alignItems='center'>
          <Box minWidth={260}>
            <DateRangePicker
              key={viewMode}
              name='date'
              control={control}
              createdAt={createdAt}
              {...(viewMode === 'insights'
                ? {
                    minDate: INSIGHTS_MIN_DATE
                  }
                : {})}
            />
          </Box>

          {/* TOGGLE */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && onViewModeChange(value)}
            sx={{
              backgroundColor: '#fff', // light grey container
              borderRadius: '999px',
              padding: '3px',
              display: 'flex',
              gap: '2px',

              '& .MuiToggleButton-root': {
                border: 'none',
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                color: '#6B7280',
                borderRadius: '999px',
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              },

              '& .Mui-selected': {
                backgroundColor: `${theme.palette.primary.main} !important`,
                color: '#fff !important',
                boxShadow: '0px 2px 6px rgba(0,0,0,0.12)' // floating effect
              }
            }}
          >
            <ToggleButton value='list'>
              <IoListOutline size={16} />
              List
            </ToggleButton>

            <ToggleButton value='insights'>
              <AiOutlineAppstore size={16} />
              Insights
            </ToggleButton>
          </ToggleButtonGroup>

          {/* SETTINGS */}
          <IconButton
            onClick={onOpenSettings}
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              bgcolor: 'white',
              width: 36,
              height: 36
            }}
          >
            <FiSettings size={24} />
          </IconButton>
        </Stack>
      </Stack>

      {/* METRIC CARDS */}
      {isMetricsLoading ? (
        <SignalsHeaderMetricsSkeleton />
      ) : (
        <Grid container spacing={2} mb={3}>
          {metricCards.map((card) => (
            <Grid key={card.key} size={{ xs: 12, sm: 6, lg: 3 }}>
              <Box
                sx={{
                  background: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  p: '16px',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  height: '100%'
                }}
              >
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <Typography fontSize={13} color='text.secondary' fontWeight={500}>
                    {card.title}
                  </Typography>

                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: theme.palette.primary.light
                    }}
                  >
                    {card.icon}
                  </Box>
                </Stack>

                <Typography mt={1.5} fontSize={28} fontWeight={600}>
                  {card.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* FILTERS */}
      {viewMode === 'list' && (
        <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
          {filterItems(keywordOptions).map((item) => (
            <Box key={item.name} sx={{ flex: 1, minWidth: 0 }}>
              <Select
                name={item.name}
                label={item.label}
                control={control}
                size='small'
                defaultValue={item.name === 'signal_state' ? 'active' : 'all'}
                sortOrder='none'
                fullWidth
                options={item.options}
                sx={{
                  background: '#fff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px'
                  }
                }}
              />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

const filterItems = (keywordOptions: string[]) => [
  {
    name: 'persona_eq',
    label: 'Persona',
    options: [
      { value: 'all', label: 'All Persona' },
      { value: 'New Customer', label: 'New Customer' },
      { value: 'Returning Customer', label: 'Returning Customer' },
      { value: 'Spam', label: 'Spam' },
      { value: 'Vendor', label: 'Vendor' },
      { value: 'Unknown', label: 'Unknown' }
    ]
  },
  {
    name: 'sentiment',
    label: 'Sentiment',
    options: [
      { value: 'all', label: 'All Sentiments' },
      { value: 'Satisfied', label: 'Satisfied' },
      { value: 'Neutral', label: 'Neutral' },
      { value: 'Frustrated', label: 'Frustrated' },
      { value: 'Churn Risk', label: 'Churn Risk' }
    ]
  },
  {
    name: 'severity',
    label: 'Severity',
    options: [
      { value: 'all', label: 'All Severities' },
      { value: 'High', label: 'High' },
      { value: 'Medium', label: 'Medium' },
      { value: 'Low', label: 'Low' }
    ]
  },
  {
    name: 'signal_state',
    label: 'Status',
    options: [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' }
    ]
  },
  {
    name: 'keywords',
    label: 'Keywords',
    options: [{ value: 'all', label: 'All' }, ...keywordOptions.map((keyword) => ({ value: keyword, label: keyword }))]
  }
];
